<template>
  <v-card flat v-if="!isBookingUpdated">
    <v-row v-if="!isMobile || (isMobile && step == bookingStepEnum.CALENDER)">
      <v-col class="justify-center align-center text-center" cols="12">
        <v-card-text class="mt-4 text-h6 font-weight-bold">{{ $t("bookingForm.BookingUpdate.update") }}</v-card-text>
        <v-card-text class="mt-n6 mb-2 headline font-weight-bold">
          {{ $t("bookingForm.BookingUpdate.yourBooking", { date: selectedSlotDate, time: selectedSlotStartTime }) }}
        </v-card-text>
      </v-col>
    </v-row>

    <v-row v-if="!isMobile">
      <v-col cols="6">
        <booking-calendar :showNavigation="false" v-on:date-change="onDateChange" />
      </v-col>
      <v-col cols="6">
        <booking-slots :showNavigation="false" @select="onSlotSelect"></booking-slots>
      </v-col>
    </v-row>

    <v-card v-else class="mx-auto" max-width="400" min-height="600" flat>
      <v-window v-model="step">
        <v-window-item :value="bookingStepEnum.CALENDER">
          <booking-calendar :showNavigation="false" v-on:date-change="onDateChange" :isMobile="true" />
        </v-window-item>
        <v-window-item :value="bookingStepEnum.SLOTS">
          <booking-slots :showNavigation="false" @select="onSlotSelect" :isMobile="true"></booking-slots>
        </v-window-item>
      </v-window>
    </v-card>
  </v-card>

  <v-card flat v-else>
    <booking-success
      heading="bookingForm.BookingUpdate.updateConfirmed"
      text="bookingForm.BookingUpdate.updateConfirmedText"
    />
  </v-card>
</template>

<script lang="ts">
import ErrorCard from "@/components/utility/ErrorCard.vue";
import { detailedDateWithDay, formatHoursAndMinutes } from "@/lib/utility/date-helper";
import {
  BookingBookingUnauthenticatedViewModelGen,
  BookingSlotViewModelGen
} from "@/services/booking/v1/data-contracts";
import { Component, Prop, Vue } from "vue-property-decorator";
import BookingCalendar from "@/components/booking/customer/BookingCalendar.vue";
import { AvailabilityModule } from "@/store/modules/availability.store";
import { NotFoundException } from "@/lib/exceptions/http";
import { handleError } from "@/lib/utility/handleError";
import BookingSlots from "@/components/booking/customer/BookingSlots.vue";
import { BookingStepEnum } from "@/store/enum/bookingScreenEnum";
import { AssetRepository, AssetEnum } from "@/lib/AssetRepository";
import BookingSuccess from "@/components/booking/customer/BookingSuccess.vue";

@Component({
  components: {
    ErrorCard,
    BookingCalendar,
    BookingSlots,
    BookingSuccess
  }
})
export default class BookingUpdate extends Vue {
  @Prop({ default: false })
  isMobile!: boolean;

  @Prop({ default: false })
  isBookingUpdated!: boolean;

  @Prop()
  bookingId!: string;

  @Prop()
  partnerId!: string;

  @Prop()
  booking!: BookingBookingUnauthenticatedViewModelGen;

  @Prop()
  serviceName!: string;

  @Prop()
  serviceLocation!: string;

  @Prop({ default: false })
  bookingCancelSuccess!: boolean;

  bookingStepEnum = BookingStepEnum;

  get step() {
    return AvailabilityModule.currentStep;
  }

  set step(slot: BookingStepEnum) {
    AvailabilityModule.setCurrentStep(slot);
  }

  get image() {
    return AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.bookingSuccess);
  }

  get selectedSlotDate() {
    if (this.booking) {
      return detailedDateWithDay(new Date(this.booking.start).toISOString(), this.$t("locale").toString());
    }

    return "";
  }

  get selectedSlotStartTime() {
    if (this.booking) {
      const from = formatHoursAndMinutes(new Date(this.booking.start));

      const to = formatHoursAndMinutes(new Date(this.booking.end));
      return this.$t("bookingForm.common.slot", { from, to });
    }

    return "";
  }

  async checkAvailability(date: Date) {
    AvailabilityModule.setIsLoadingSlots(true);

    try {
      await AvailabilityModule.findOneByPartner({
        partnerId: this.partnerId,
        id: AvailabilityModule.serviceId || this.booking.serviceId,
        date: date?.toISOString() || new Date().toISOString()
      });
    } catch (error) {
      if (error instanceof NotFoundException) {
        Vue.$toast.error(String(this.$t("bookingForm.BookingProcess.serviceNotFound")));
      } else {
        handleError(error);
      }
    } finally {
      AvailabilityModule.setIsLoadingSlots(false);
    }
  }

  async onDateChange(date: Date) {
    /** Fetch availability when changing dates in the calendar */
    await this.checkAvailability(date);
  }

  onSlotSelect(selectedSlot: BookingSlotViewModelGen) {
    this.$emit("click", selectedSlot);
  }
}
</script>
<style scoped>
.scrollable-content {
  overflow-y: auto;
}

::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 7px;
}

::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
</style>
